import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useClientLicenses } from "@/hooks/useClientLicenses";
import { useModulePrices } from "@/hooks/useModulePrices";
import { ClientLicense } from "@/types/license";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";

interface EditClientLicenseDialogProps {
  license: ClientLicense | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditClientLicenseDialog = ({ license, open, onOpenChange }: EditClientLicenseDialogProps) => {
  const { updateClientLicense, isUpdatingLicense } = useClientLicenses();
  const { modulePrices } = useModulePrices();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>('active');
  const [billingCycle, setBillingCycle] = useState<string>('monthly');
  const [contractedSeats, setContractedSeats] = useState(1);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [trialEndDate, setTrialEndDate] = useState<Date>();
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // Estados para configuração de IA
  const [aiProvider, setAiProvider] = useState<'anthropic' | 'openai' | 'local'>('anthropic');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiApiUrl, setAiApiUrl] = useState('http://localhost:11434');
  const [aiModelName, setAiModelName] = useState('');

  // Fetch current client modules
  const { data: currentModules = [] } = useQuery({
    queryKey: ['client-modules-edit', license?.client_id],
    queryFn: async () => {
      if (!license?.client_id) return [];
      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('client_id', license.client_id)
        .eq('is_active', true);
      if (error) throw error;
      return data;
    },
    enabled: !!license?.client_id && open,
  });

  // Verificar se módulo IA Integrada está ativo ou selecionado
  const hasAIIntegration = selectedModules.includes('ai_integrated');

  // Buscar configuração de IA existente
  const { data: existingAIConfig } = useQuery({
    queryKey: ['ai-config-client', license?.client_id],
    queryFn: async () => {
      if (!license?.client_id) return null;
      const { data, error } = await supabase
        .from('ai_config')
        .select('*')
        .eq('client_id', license.client_id)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!license?.client_id && open && hasAIIntegration,
  });

  const calculateEndDate = (start: Date, cycle: string): Date => {
    const end = new Date(start);
    if (cycle === 'monthly') {
      end.setDate(end.getDate() + 30);
    } else {
      end.setDate(end.getDate() + 365);
    }
    return end;
  };

  useEffect(() => {
    if (license) {
      setStatus(license.status);
      setBillingCycle(license.billing_cycle);
      setContractedSeats(license.contracted_seats || 1);
      const start = new Date(license.start_date);
      setStartDate(start);
      if (license.end_date) {
        setEndDate(new Date(license.end_date));
      } else if (license.start_date) {
        setEndDate(calculateEndDate(start, license.billing_cycle));
      }
      setTrialEndDate(license.trial_end_date ? new Date(license.trial_end_date) : undefined);
    }
  }, [license]);

  // Recalcular endDate quando billingCycle ou startDate mudar
  useEffect(() => {
    if (startDate && license && !license.end_date) {
      setEndDate(calculateEndDate(startDate, billingCycle));
    }
  }, [billingCycle, startDate]);

  useEffect(() => {
    const codes = currentModules.map(m => m.module_code).sort().join(',');
    setSelectedModules(prev => {
      const prevCodes = [...prev].sort().join(',');
      return prevCodes === codes ? prev : currentModules.map(m => m.module_code);
    });
  }, [currentModules]);

  // Carregar configuração de IA existente
  useEffect(() => {
    if (existingAIConfig) {
      setAiProvider(existingAIConfig.provider as any);
      setAiApiKey(existingAIConfig.api_key || '');
      setAiApiUrl(existingAIConfig.api_url || 'http://localhost:11434');
      setAiModelName(existingAIConfig.model_name || '');
    } else {
      // Reset para valores padrão
      setAiProvider('anthropic');
      setAiApiKey('');
      setAiApiUrl('http://localhost:11434');
      setAiModelName('');
    }
  }, [existingAIConfig]);

  const toggleModule = (code: string) => {
    setSelectedModules(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!license || !startDate) return;

    setIsSaving(true);
    try {
      // Update license
      updateClientLicense({
        id: license.id,
        licenseData: {
          status: status as any,
          billing_cycle: billingCycle as any,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
          trial_end_date: trialEndDate ? format(trialEndDate, 'yyyy-MM-dd') : undefined,
          contracted_seats: contractedSeats,
        }
      });

      // Sync modules: deactivate removed, activate added
      const currentCodes = currentModules.map(m => m.module_code);
      const toDeactivate = currentCodes.filter(c => !selectedModules.includes(c));
      const toActivate = selectedModules.filter(c => !currentCodes.includes(c));

      if (toDeactivate.length > 0) {
        await supabase
          .from('client_modules')
          .update({ is_active: false, deactivated_at: new Date().toISOString() })
          .eq('client_id', license.client_id)
          .in('module_code', toDeactivate);
      }

      for (const code of toActivate) {
        await supabase
          .from('client_modules')
          .upsert({
            client_id: license.client_id,
            module_code: code,
            is_active: true,
            activated_at: new Date().toISOString(),
            source: 'admin',
          }, { onConflict: 'client_id,module_code' });
      }

      queryClient.invalidateQueries({ queryKey: ['client-modules'] });
      queryClient.invalidateQueries({ queryKey: ['all-client-modules'] });

      // Salvar configuração de IA se módulo está ativo
      if (hasAIIntegration) {
        // Validar campos obrigatórios
        if (aiProvider !== 'local' && !aiApiKey) {
          toast({ 
            title: "Configuração de IA incompleta", 
            description: "API Key é obrigatória para Anthropic e OpenAI", 
            variant: "destructive" 
          });
          setIsSaving(false);
          return;
        }

        if (aiProvider === 'local' && !aiApiUrl) {
          toast({ 
            title: "Configuração de IA incompleta", 
            description: "URL da API é obrigatória para IA Local", 
            variant: "destructive" 
          });
          setIsSaving(false);
          return;
        }

        // Desativar configurações antigas do cliente
        await supabase
          .from('ai_config')
          .update({ is_active: false })
          .eq('client_id', license.client_id);

        // Criar ou atualizar configuração
        const aiConfigData = {
          client_id: license.client_id,
          provider: aiProvider,
          api_key: aiProvider !== 'local' ? aiApiKey : null,
          api_url: aiProvider === 'local' ? aiApiUrl : null,
          model_name: aiModelName || null,
          is_active: true,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        };

        if (existingAIConfig) {
          // Atualizar existente
          await supabase
            .from('ai_config')
            .update(aiConfigData)
            .eq('id', existingAIConfig.id);
        } else {
          // Criar novo
          await supabase
            .from('ai_config')
            .insert(aiConfigData);
        }

        queryClient.invalidateQueries({ queryKey: ['ai-config-client'] });
      }

      toast({ title: "Assinatura atualizada", description: "Alterações salvas com sucesso." });
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const activeModules = modulePrices.filter(m => m.is_active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Assinatura</DialogTitle>
          <DialogDescription>Edite os detalhes da assinatura do cliente</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label>Cliente</Label>
            <div className="p-2 bg-muted rounded-md">{license?.client?.name}</div>
          </div>

          {/* Módulos */}
          <div>
            <Label className="mb-2 block">Módulos</Label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
              {activeModules.map(mod => (
                <div key={mod.code} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-mod-${mod.code}`}
                    checked={selectedModules.includes(mod.code)}
                    onCheckedChange={() => toggleModule(mod.code)}
                  />
                  <label htmlFor={`edit-mod-${mod.code}`} className="text-sm cursor-pointer">
                    {mod.name} (R$ {mod.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Licenças (Seats)</Label>
              <Input type="number" min="1" value={contractedSeats} onChange={(e) => setContractedSeats(parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativa</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="suspended">Suspensa</SelectItem>
                  <SelectItem value="cancelled">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ciclo</Label>
              <Select value={billingCycle} onValueChange={setBillingCycle}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensal</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data de Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP", { locale: ptBR }) : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Data de Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP", { locale: ptBR }) : "Sem data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Configuração de IA (condicional) */}
          {hasAIIntegration && (
            <div className="border-t pt-4 mt-4">
              <Label className="text-lg font-semibold mb-4 block">Configuração de IA</Label>
              
              <div className="space-y-4">
                <div>
                  <Label>Provedor de IA</Label>
                  <Select value={aiProvider} onValueChange={(v) => setAiProvider(v as any)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                      <SelectItem value="openai">OpenAI (GPT)</SelectItem>
                      <SelectItem value="local">Local (Ollama/LM Studio)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {aiProvider === 'anthropic' && (
                  <>
                    <div>
                      <Label>API Key *</Label>
                      <Input
                        type="password"
                        placeholder="sk-ant-..."
                        value={aiApiKey}
                        onChange={(e) => setAiApiKey(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Obtenha em: <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">console.anthropic.com</a>
                      </p>
                    </div>
                    <div>
                      <Label>Modelo (opcional)</Label>
                      <Input
                        placeholder="claude-3-5-sonnet-20241022"
                        value={aiModelName}
                        onChange={(e) => setAiModelName(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {aiProvider === 'openai' && (
                  <>
                    <div>
                      <Label>API Key *</Label>
                      <Input
                        type="password"
                        placeholder="sk-..."
                        value={aiApiKey}
                        onChange={(e) => setAiApiKey(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Obtenha em: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">platform.openai.com/api-keys</a>
                      </p>
                    </div>
                    <div>
                      <Label>Modelo (opcional)</Label>
                      <Input
                        placeholder="gpt-4-turbo-preview"
                        value={aiModelName}
                        onChange={(e) => setAiModelName(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {aiProvider === 'local' && (
                  <>
                    <div>
                      <Label>URL da API *</Label>
                      <Input
                        placeholder="http://localhost:11434"
                        value={aiApiUrl}
                        onChange={(e) => setAiApiUrl(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        URL do Ollama ou LM Studio rodando localmente
                      </p>
                    </div>
                    <div>
                      <Label>Modelo (opcional)</Label>
                      <Input
                        placeholder="llama2"
                        value={aiModelName}
                        onChange={(e) => setAiModelName(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isUpdatingLicense || isSaving}>Salvar Alterações</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
